import React, { Fragment, useEffect, useState } from "react";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import { useEventBookingOptions } from "../hooks/useEventBookingOptions";
import { createBooking } from "../api/apiClient";
import { useStateContext } from "../contexts/ContextProvider";
import { toast } from "react-toastify";
import { BOOKING_ERROR_MESSAGES } from "../utility/bookingErrorMessages";
import { useUnattendedAccessTerms } from "../hooks/useUnattendedAccessTerms";
import UnattendedAccessTermsModal from "../components/UnattendedAccessTermsModal";
import CheckoutSteps from "../components/CheckoutSteps";
import HtmlContent from "../components/HtmlContent";
import {
    getLowStockLabel,
    isProductSoldOut,
    PRODUCT_SOLD_OUT_ERROR_CODE,
    PRODUCT_SOLD_OUT_LABEL,
} from "../utility/productAvailability";

const EventBookingProductsPage = () => {
    const { eventUuid } = useParams();
    const { event, customerProducts, products } = useEventBookingOptions(eventUuid);
    const [selectedOption, setSelectedOption] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const { token } = useStateContext();
    const navigate = useNavigate();
    const location = useLocation();
    const unattendedAccessTerms = useUnattendedAccessTerms();

    useEffect(() => {
        if (token && customerProducts.length > 0) {
            setSelectedOption(customerProducts[0].uuid);
            return;
        }

        const firstAvailableProduct = products.find((product) => !isProductSoldOut(product));

        if (token && firstAvailableProduct) {
            setSelectedOption(firstAvailableProduct.uuid);
        }
    }, [customerProducts, products, token]);

    if (!event || Object.keys(event).length === 0) {
        return <div>Loading...</div>;
    }

    const handleLoginRedirect = () => {
        navigate("/login", { state: { from: location.pathname } });
    };

    const handleContinue = async () => {
        if (!selectedOption) {
            toast.info("Bitte wähle ein Produkt aus.");
            return;
        }

        setSubmitting(true);

        try {
            if (customerProducts.some((product) => product.uuid === selectedOption)) {
                const response = await createBooking(eventUuid);
                navigate(`/bookings/${response}/successful`);
            } else if (products.some((product) => product.uuid === selectedOption)) {
                const selectedProduct = products.find((product) => product.uuid === selectedOption);

                if (isProductSoldOut(selectedProduct)) {
                    toast.error(BOOKING_ERROR_MESSAGES[PRODUCT_SOLD_OUT_ERROR_CODE]);
                    return;
                }

                navigate(`/events/${eventUuid}/products/${selectedOption}/checkout`);
            }
        } catch (error) {
            console.error("Failed to create booking:", error);

            let errorData;
            try {
                errorData = JSON.parse(error.message);
            } catch (parseError) {
                errorData = null;
            }

            if (errorData?.status === 400 && errorData?.code === 808) {
                unattendedAccessTerms.promptForTerms(handleContinue);
                return;
            }

            const mappedMessage = errorData?.status === 400 && BOOKING_ERROR_MESSAGES[errorData.code];

            toast.error(mappedMessage || "Fehler bei der Buchung. Bitte versuche es erneut.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto max-w-4xl p-6 bg-white rounded-lg shadow-md">
            {/* Zurück Link */}
            <div className="mb-6">
                <button
                    className="text-pegasusPurple hover:text-pink-700 font-medium"
                    onClick={() => navigate(-1)}
                >
                    &lt; Zurück
                </button>
            </div>

            <div className="mb-6">
                <CheckoutSteps currentStep={1}/>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Right Column: Order Summary */}
                <div className="order-1 md:order-2">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Deine Bestellung</h2>
                    <div className="border p-4 rounded-lg shadow-md">
                        <img
                            src={event.image}
                            alt={event.name}
                            className="w-full h-32 object-cover rounded-md mb-4"
                        />
                        <h3 className="font-bold text-gray-800">{event.name}</h3>
                        <p className="text-sm text-gray-600">
                            {new Date(event.eventDate).toLocaleDateString("de-DE", {
                                weekday: "long",
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                            })}{" "}
                            | {event.startTime} - {event.endTime}
                        </p>
                        <p className="text-sm text-gray-600">
                            {event.trainerNames} · {event.locationName}
                        </p>
                        <p className="text-sm text-gray-600 mt-2">{event.freeSeats} verfügbar</p>
                    </div>
                    {/* Desktop Weiter Button */}
                    <button
                        disabled={!token || submitting}
                        onClick={handleContinue}
                        className={`hidden md:flex w-full mt-4 py-2 rounded-lg font-semibold items-center justify-center ${
                            token && !submitting
                                ? "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                                : "bg-gray-300 text-gray-600 cursor-not-allowed"
                        }`}
                    >
                        {submitting && (
                            <span className="inline-block w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                        )}
                        {submitting ? "Wird verarbeitet..." : "WEITER"}
                    </button>
                </div>

                {/* Left Column: Product Selection */}
                <div className="col-span-2 order-2 md:order-1">
                    {!token ? (
                        <Fragment>
                            <h2 className="text-xl font-bold text-gray-800 mb-4">
                                Bitte einloggen, um fortzufahren
                            </h2>
                            <div className="flex justify-center mb-6">
                                <button
                                    onClick={handleLoginRedirect}
                                    className="bg-pegasusPurple text-white py-2 px-4 rounded-lg font-semibold hover:bg-pegasusPurpleHover"
                                >
                                    EINLOGGEN
                                </button>
                            </div>
                            <div className="space-y-4 mt-6">
                                {products.map((product) => {
                                    const soldOut = isProductSoldOut(product);
                                    const lowStockLabel = getLowStockLabel(product);

                                    return (
                                        <div
                                            key={product.uuid}
                                            className={`flex items-center border p-4 rounded-lg ${soldOut ? "opacity-60" : ""}`}
                                        >
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-800">{product.name}</h3>
                                                <HtmlContent className="text-sm text-gray-600" html={product.description}/>
                                                {lowStockLabel && (
                                                    <p className="text-sm font-medium text-pegasusPurple mt-1">{lowStockLabel}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-semibold ${soldOut ? "text-gray-500 line-through" : "text-gray-800"}`}>
                                                    {product.bruttoPrice.toFixed(2)} {product.currencySign}
                                                </p>
                                                {soldOut ? (
                                                    <span className="inline-block mt-1 bg-gray-200 text-gray-700 text-sm font-semibold px-2 py-1 rounded">
                                                        {PRODUCT_SOLD_OUT_LABEL}
                                                    </span>
                                                ) : (
                                                    <p className="text-gray-500 text-sm">Einmalig</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment>
                            {customerProducts.length > 0 && (
                                <Fragment>
                                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                                        Verwende deine Produkte
                                    </h2>
                                    <div className="space-y-4">
                                        {customerProducts.map((product) => (
                                            <label
                                                key={product.uuid}
                                                className="flex items-center border p-4 rounded-lg cursor-pointer hover:border-pegasusPurple"
                                            >
                                                <input
                                                    type="radio"
                                                    name="product"
                                                    className="mr-4"
                                                    value={product.uuid}
                                                    checked={selectedOption === product.uuid}
                                                    onChange={() => setSelectedOption(product.uuid)}
                                                />
                                                <div>
                                                    <h3 className="font-semibold text-gray-800">{product.name}</h3>
                                                    <p className="text-sm text-gray-600">{product.credit - product.creditUsed} Besuche übrig</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </Fragment>
                            )}

                            {/* Kaufe neue Produkte */}
                            <h2 className="text-xl font-bold text-gray-800 mt-8 mb-4">Kaufe neue Produkte</h2>
                            <div className="space-y-4">
                                {products.map((product) => {
                                    const soldOut = isProductSoldOut(product);
                                    const lowStockLabel = getLowStockLabel(product);

                                    return (
                                        <label
                                            key={product.uuid}
                                            className={`flex items-center border p-4 rounded-lg ${
                                                soldOut
                                                    ? "opacity-60 cursor-not-allowed"
                                                    : "cursor-pointer hover:border-pegasusPurple"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="product"
                                                className="mr-4"
                                                value={product.uuid}
                                                disabled={soldOut}
                                                checked={selectedOption === product.uuid}
                                                onChange={() => setSelectedOption(product.uuid)}
                                            />
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-800">{product.name}</h3>
                                                <HtmlContent className="text-sm text-gray-600" html={product.description}/>
                                                {lowStockLabel && (
                                                    <p className="text-sm font-medium text-pegasusPurple mt-1">{lowStockLabel}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-semibold ${soldOut ? "text-gray-500 line-through" : "text-gray-800"}`}>
                                                    {product.bruttoPrice.toFixed(2)} {product.currencySign}
                                                </p>
                                                {soldOut ? (
                                                    <span className="inline-block mt-1 bg-gray-200 text-gray-700 text-sm font-semibold px-2 py-1 rounded">
                                                        {PRODUCT_SOLD_OUT_LABEL}
                                                    </span>
                                                ) : (
                                                    <p className="text-gray-500 text-sm">Einmalig</p>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </Fragment>
                    )}
                </div>
            </div>

            {/* Mobile Weiter Button */}
            <div className="block md:hidden mt-6">
                <button
                    disabled={!token || submitting}
                    onClick={handleContinue}
                    className={`w-full py-2 rounded-lg font-semibold flex items-center justify-center ${
                        token && !submitting
                            ? "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                            : "bg-gray-300 text-gray-600 cursor-not-allowed"
                    }`}
                >
                    {submitting && (
                        <span className="inline-block w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                    )}
                    {submitting ? "Wird verarbeitet..." : "WEITER"}
                </button>
            </div>

            <UnattendedAccessTermsModal
                isOpen={unattendedAccessTerms.isOpen}
                terms={unattendedAccessTerms.terms}
                loading={unattendedAccessTerms.loading}
                accepting={unattendedAccessTerms.accepting}
                error={unattendedAccessTerms.error}
                onAccept={unattendedAccessTerms.acceptTerms}
                onClose={unattendedAccessTerms.closeModal}
            />
        </div>
    );
};

export default EventBookingProductsPage;
